import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { WizardFieldError } from "@ui/components/Wizard";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { NumberField, NumberFieldGroup, NumberFieldInput } from "@ui/primitives/NumberField";
import { type ReactNode, useId } from "react";

const stepperButton = tv({
  base: [
    "flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white text-neutral-500",
    "shadow-[0_1px_0_0_rgba(0,0,0,0.16),0_0_0_1px_rgba(0,0,0,0.12)]",
    "transition-opacity",
    "disabled:cursor-not-allowed disabled:text-neutral-500/50 disabled:opacity-75",
  ],
});

const valueText = "font-display text-3xl font-normal leading-10 tracking-display-md text-neutral-800 md:text-4xl";

export interface NumberStepperProps {
  /** Current value */
  value: number;
  /** Callback when value changes */
  onChange: (value: number) => void;
  /** Minimum allowed value */
  min?: number;
  /** Maximum allowed value */
  max?: number;
  /** Step increment/decrement amount */
  step?: number;
  /** Unit label displayed after the value (e.g., "Years", "Hubs") */
  unit?: string;
  /** Accessible label for the stepper */
  "aria-label"?: string;
  /** Marks the field invalid for AT (aria-invalid) and shows `errorMessage` below the control. */
  isInvalid?: boolean;
  /** Centred wizard-style message, associated with the input via aria-describedby. */
  errorMessage?: ReactNode;
}

/**
 * A numeric stepper control with circular increment/decrement buttons around a typed-entry
 * input. Values clamp to min/max on commit; the bound buttons auto-disable.
 */
export function NumberStepper({
  value,
  onChange,
  min = 1,
  max = 99,
  step = 1,
  unit,
  "aria-label": ariaLabel = "Number",
  isInvalid,
  errorMessage,
}: NumberStepperProps) {
  const errorId = useId();
  const showError = Boolean(isInvalid && errorMessage);

  return (
    <NumberField
      aria-describedby={showError ? errorId : undefined}
      aria-label={ariaLabel}
      className="flex flex-col items-center"
      isInvalid={isInvalid}
      maxValue={max}
      minValue={min}
      // An emptied input commits NaN; never leak it to consumers.
      onChange={(next) => {
        if (!Number.isNaN(next)) onChange(next);
      }}
      step={step}
      value={value}
    >
      <NumberFieldGroup className="flex items-center gap-8">
        <Button aria-label={t("components.number_stepper.decrease")} className={stepperButton()} slot="decrement">
          <Icon name="minus" size="24" />
        </Button>

        <NumberFieldInput className={`w-[2ch] min-w-[22px] bg-transparent text-center outline-hidden ${valueText}`} />

        <Button aria-label={t("components.number_stepper.increase")} className={stepperButton()} slot="increment">
          <Icon name="plus" size="24" />
        </Button>

        {unit && <span className={valueText}>{unit}</span>}
      </NumberFieldGroup>
      {showError ? <WizardFieldError id={errorId}>{errorMessage}</WizardFieldError> : null}
    </NumberField>
  );
}
